import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { useActiveSection } from '../../hooks/useActiveSection';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { Button } from '../ui/Button';
import styles from './Navbar.module.css';

export const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const scrolled = useScrollPosition(80);
  const scrollDirection = useScrollDirection();
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Navbar is visible if:
  // 1. We are in the initial mount animation phase (let CSS handle it)
  // 2. Mobile menu is open
  // 3. We are close to the top of the page (scrolled is false)
  // 4. We are scrolling up
  const isNavbarVisible = !mounted || mobileOpen || !scrolled || scrollDirection === 'up';

  /** Links that scroll to sections on the home page */
  const sectionLinks = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.about'), path: '/#about' },
    { label: t('nav.services'), path: '/#services' },
    { label: t('nav.contact'), path: '/#contact' },
  ];

  /** Links that navigate to standalone pages */
  const pageLinks = [
    { label: t('nav.research'), path: '/research' },
    { label: t('nav.portfolio'), path: '/portfolio' },
  ];

  /** Combined for observer & shared helpers */
  const navItems = [...sectionLinks, ...pageLinks];

  // Extract section IDs from anchor-based nav items for IntersectionObserver
  const sectionIds = useMemo(
    () =>
      navItems
        .filter((item) => item.path.startsWith('/#'))
        .map((item) => item.path.replace('/#', '')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const activeSection = useActiveSection(sectionIds);

  const isActive = (path: string) => {
    // Home: active only when at the top of the page (hero visible)
    if (path === '/') {
      return location.pathname === '/' && !scrolled && !activeSection;
    }
    // Anchor links: active based on IntersectionObserver
    if (path.startsWith('/#')) {
      const sectionId = path.replace('/#', '');
      return activeSection === sectionId;
    }
    // Route links (e.g. /blog): active based on pathname match
    return location.pathname.startsWith(path);
  };

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleNavClick = useCallback(
    (path: string) => {
      if (path === '/') {
        if (location.pathname === '/') {
          // Already on home — scroll to top
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // If on another page, the <Link to="/"> navigation + ScrollToTop handles it
        return;
      }

      if (path.startsWith('/#')) {
        const id = path.replace('/#', '');

        if (location.pathname === '/') {
          // Already on home page — just scroll
          scrollToSection(id);
        } else {
          // On another page — navigate home first, then scroll after render
          navigate('/');
          // Wait for the home page to mount and render sections
          requestAnimationFrame(() => {
            setTimeout(() => scrollToSection(id), 150);
          });
        }
      }
    },
    [location.pathname, navigate, scrollToSection]
  );

  return (
    <nav
      className={`${styles.nav} ${
        scrolled || location.pathname !== '/' ? styles.navScrolled : styles.navTransparent
      } ${
        isNavbarVisible ? styles.navVisible : styles.navHidden
      }`}
    >
      <div className={styles.inner}>
        {/* Logo */}
        <Link to="/" className={styles.logoLink}>
          <img
            src="/logo.svg"
            alt="NexTrainE"
            className={styles.logoImg}
          />
          <span className={`${styles.logoText} ${styles.logoTextDark}`}>
            <span className={styles.logoCyan}>N</span>ex
            <span className={styles.logoCyan}>T</span>rain
            <span className={styles.logoCyan}>E</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className={styles.desktopLinks}>
          {sectionLinks.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={(e) => {
                if (item.path === '/' || item.path.startsWith('/#')) {
                  if (item.path === '/' && location.pathname !== '/') return;
                  e.preventDefault();
                  handleNavClick(item.path);
                }
              }}
              className={`${styles.navLink} ${styles.navLinkDark} ${
                isActive(item.path) ? styles.navLinkDarkActive : ''
              }`}
            >
              {item.label}
            </Link>
          ))}

          {/* Pipe divider */}
          <span className={styles.pipeDivider} aria-hidden="true" />

          {pageLinks.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.navLink} ${styles.navLinkDark} ${
                isActive(item.path) ? styles.navLinkDarkActive : ''
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Right Side */}
        <div className={styles.rightSide}>
          <LanguageSwitcher />
          <div className={styles.ctaDesktop}>
            <Button variant="primary" size="small" href="/#contact">
              {t('nav.cta')}
            </Button>
          </div>

          {/* Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`${styles.hamburger} ${mobileOpen ? styles.hamburgerOpen : ''}`}
            aria-label="Toggle menu"
          >
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`${styles.mobileMenu} ${mobileOpen ? styles.mobileMenuOpen : ''}`}>
        {sectionLinks.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={(e) => {
              if (item.path === '/' || item.path.startsWith('/#')) {
                if (item.path === '/' && location.pathname !== '/') {
                  setMobileOpen(false);
                  return;
                }
                e.preventDefault();
                handleNavClick(item.path);
              }
              setMobileOpen(false);
            }}
            className={`${styles.mobileLink} ${isActive(item.path) ? styles.mobileLinkActive : ''}`}
          >
            {item.label}
          </Link>
        ))}

        {/* Mobile pipe divider */}
        <div className={styles.mobileDivider} aria-hidden="true" />

        {pageLinks.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={`${styles.mobileLink} ${isActive(item.path) ? styles.mobileLinkActive : ''}`}
          >
            {item.label}
          </Link>
        ))}

        <div className={styles.mobileCta}>
          <Button variant="primary" href="/#contact">
            {t('nav.cta')}
          </Button>
        </div>
      </div>
    </nav>
  );
};
